/**
 * Shared defaults for Upsell & Recommendation product popups (catalog + freeform).
 * Used by editor normalize, AI layout compat, and template seeds.
 */
import {
  DEFAULT_PRICE_DISPLAY,
  DEFAULT_VARIANT_PICKER_DISPLAY,
  getCoverImageDisplay,
  getRatingDisplay,
  PRICE_LAYOUT,
} from "./product-block-display.js";
import { applyCatalogUpsellProductSubsectionAlign } from "./text-block-layout.js";
import { isSectionVisible, setSectionVisibility } from "./section-visibility.js";
import { resolvePopupEditorCategoryKey } from "./template-library-categories.js";
import { POPUP_LAYOUT_IDS, resolvePopupLayoutId } from "./popup-layouts/registry.js";
import { ensureProductShellLayout, PRODUCT_SHELL_LAYOUT } from "./product-shell-layout.js";

const EXTENSION_POPUP_TYPES = new Set([
  "checkout_extension_upsell_widget",
  "post_purchase_widget",
  "thankyou_widget",
  "order_status_widget",
  "cart_drawer_widget",
]);

function isExtensionPopup(popup) {
  if (!popup) return false;
  if (EXTENSION_POPUP_TYPES.has(String(popup.popUpType || ""))) return true;
  const primary = String(popup?.category?.primary || "").toLowerCase();
  const key = String(popup?.category?.key || "").toLowerCase();
  return primary.includes("checkout extension") || key === "checkout-extensions";
}

export const PRODUCT_UPSELL_PRIMARY_CTA_TEXT = "Add to Order";

/** Same contract as AI Recommendation Upsell primary CTA (ButtonBlock form_submit). */
export const PRODUCT_UPSELL_PRIMARY_CTA_ACTION = Object.freeze({
  type: "form_submit",
  parameters: { event: "product_add_cart" },
});

/** Product page Carousel cards — second CTA that adds then redirects to checkout. */
export const PRODUCT_UPSELL_BUY_NOW_CTA_TEXT = "Buy Now";
export const PRODUCT_UPSELL_BUY_NOW_CTA_ROLE = "buyNowCta";
/** Shopify native checkout redirect. */
export const BUY_NOW_ACTION_CHECKOUT = "checkout";
/** Fastrr / Shiprocket mini-cart via shiprocketCheckoutEvents.buyCart. */
export const BUY_NOW_ACTION_SHIPROCKET = "shiprocket";
/** GoKwik / KwikCheckout via gokwikSdk / triggerGokwikCustomCheckout. */
export const BUY_NOW_ACTION_GOKWIK = "gokwik";
/** Breeze checkout popup via window.breeze.startCheckout. */
export const BUY_NOW_ACTION_BREEZE = "breeze";
/** Razorpay Magic Checkout via theme checkout button / magic-shopify.js. */
export const BUY_NOW_ACTION_RAZORPAY = "razorpay";
export const BUY_NOW_ACTION_OPTIONS = Object.freeze([
  { value: BUY_NOW_ACTION_CHECKOUT, label: "Checkout page" },
  { value: BUY_NOW_ACTION_SHIPROCKET, label: "Shiprocket" },
  { value: BUY_NOW_ACTION_GOKWIK, label: "GoKwik" },
  { value: BUY_NOW_ACTION_BREEZE, label: "Breeze" },
  { value: BUY_NOW_ACTION_RAZORPAY, label: "Razorpay" },
]);

/** Normalize Buy Now / cart checkout destination action. */
export function normalizeCheckoutDestinationAction(value) {
  if (value === BUY_NOW_ACTION_SHIPROCKET) return BUY_NOW_ACTION_SHIPROCKET;
  if (value === BUY_NOW_ACTION_GOKWIK) return BUY_NOW_ACTION_GOKWIK;
  if (value === BUY_NOW_ACTION_BREEZE) return BUY_NOW_ACTION_BREEZE;
  if (value === BUY_NOW_ACTION_RAZORPAY) return BUY_NOW_ACTION_RAZORPAY;
  return BUY_NOW_ACTION_CHECKOUT;
}

export const CHECKOUT_DESTINATION_ACTION_DETAILS =
  "Shiprocket, GoKwik, Breeze, and Razorpay only work if that checkout app is installed on your store. If it is not set up, checkout uses the Checkout page by default.";

export const PRODUCT_UPSELL_BUY_NOW_CTA_ACTION = Object.freeze({
  type: "form_submit",
  parameters: {
    event: "product_check_out",
    buyNowAction: BUY_NOW_ACTION_CHECKOUT,
  },
});

const ADD_TO_CART_TEXT_PATTERN =
  /add\s*to\s*(cart|order)|^\+?\s*add\s*\+?$/i;

function getProductButtonDisplayText(section) {
  const raw = section?.text;
  if (typeof raw === "string") return raw.trim();
  if (raw && typeof raw === "object") {
    return String(raw.html || raw.text || "").trim();
  }
  return "";
}

/** Canonical product catalog block order (editor tree, chips, preview flow). */
export const PRODUCT_CATALOG_SUBSECTION_ORDER = Object.freeze([
  "COVER_IMAGE",
  "TITLE",
  "RATING",
  "PRICE",
  "VARIANT_PICKER",
  "QUANTITY_PICKER",
  "DESCRIPTION",
]);

/**
 * Stable sort for product inner blocks — known catalog types first, others unchanged at end.
 * @param {object[] | null | undefined} sections
 * @returns {object[]}
 */
export function sortProductCatalogSubsections(sections) {
  if (!Array.isArray(sections)) return sections;
  const rank = new Map(
    PRODUCT_CATALOG_SUBSECTION_ORDER.map((type, index) => [type, index])
  );
  return [...sections].sort((a, b) => {
    const rankA = rank.has(a?.type) ? rank.get(a.type) : 1000;
    const rankB = rank.has(b?.type) ? rank.get(b.type) : 1000;
    return rankA - rankB;
  });
}

export const PRODUCT_UPSELL_LEGACY_CTA_TEXTS = Object.freeze([
  "Add to Order — Save 30%",
  "Add to cart — Save 30%",
  "Add to Cart — Save 30%",
]);

/** Session timer (editor label: “Fixed”) — 5:00 countdown. */
export const PRODUCT_UPSELL_COUNTDOWN_DURATION = Object.freeze({
  days: 0,
  hours: 0,
  minutes: 5,
  seconds: 0,
});

const ADD_TO_CART_FORM_EVENTS = new Set([
  "product_add_cart",
  "product_add_cart_close_popup",
  "product_add_cart_refresh_the_window",
  "product_add_cart_open_the_custom_cart_drawer",
]);

export function popupSchemaHasProductBlock(popup) {
  const steps = popup?.schema;
  if (!Array.isArray(steps)) return false;
  return steps.some((step) => {
    const sections = step?.section || step?.sections;
    return Array.isArray(sections) && sections.some((s) => s?.type === "PRODUCT");
  });
}

/**
 * Storefront overlay popups with a PRODUCT block (excludes checkout extensions + cart drawer).
 */
export function isStorefrontProductPopup(popup) {
  if (!popup || typeof popup !== "object") return false;
  if (popup?.generalSettings?.isStaticCartDrawer) return false;
  if (popup?.generalSettings?.isStaticProductPageUpsell) return false;

  const layoutId = resolvePopupLayoutId(popup);
  if (layoutId === POPUP_LAYOUT_IDS.CART_DRAWER) return false;
  if (layoutId === POPUP_LAYOUT_IDS.PRODUCT_PAGE_UPSELL) return false;
  if (isExtensionPopup(popup)) return false;

  return popupSchemaHasProductBlock(popup);
}

/**
 * Upsell & Recommendation goal popups (upsell-crosssell) with a PRODUCT block.
 * Excludes cart drawer and checkout extension surfaces.
 */
export function isUpsellRecommendationProductPopup(popup) {
  if (!isStorefrontProductPopup(popup)) return false;

  const categoryKey = resolvePopupEditorCategoryKey(popup);
  return categoryKey === "upsell-crosssell";
}

function resolveProductUpsellCtaText(text, fallback = PRODUCT_UPSELL_PRIMARY_CTA_TEXT) {
  const trimmed = String(text ?? "").trim();
  if (!trimmed || PRODUCT_UPSELL_LEGACY_CTA_TEXTS.includes(trimmed)) {
    return fallback;
  }
  return trimmed;
}

export function isProductAddToCartButton(section) {
  if (!section || section.type !== "BUTTON") return false;
  const action = section?.setting?.action || {};
  if (action.type === "add_to_cart") return true;
  if (
    action.type === "form_submit" &&
    ADD_TO_CART_FORM_EVENTS.has(action.parameters?.event)
  ) {
    return true;
  }
  if (section?.setting?.role === "primaryCta") return true;
  return false;
}

/** Detect product CTA buttons even when action config was never seeded on the template. */
export function looksLikeProductAddToCartButton(section) {
  if (!section || section.type !== "BUTTON") return false;
  if (isProductAddToCartButton(section)) return true;
  return ADD_TO_CART_TEXT_PATTERN.test(getProductButtonDisplayText(section));
}

/**
 * Ensure add-to-cart buttons fire form_submit + count toward conversion/revenue tracking.
 * @param {object | null | undefined} section
 */
export function ensureProductUpsellAddToCartButtonSettings(section) {
  if (!looksLikeProductAddToCartButton(section)) return section;

  section.setting = section.setting || {};
  if (!isProductAddToCartButton(section)) {
    section.setting.action = {
      ...PRODUCT_UPSELL_PRIMARY_CTA_ACTION,
      ...(section.setting.action || {}),
      type: PRODUCT_UPSELL_PRIMARY_CTA_ACTION.type,
      parameters: {
        ...(section.setting.action?.parameters || {}),
        ...PRODUCT_UPSELL_PRIMARY_CTA_ACTION.parameters,
      },
    };
  }
  if (section.setting.isConversationEnabled !== false) {
    section.setting.isConversationEnabled = true;
  }
  if (!section.setting.btn_type?.type) {
    section.setting.btn_type = {
      ...(section.setting.btn_type || {}),
      type: "text",
    };
  }
  section.text = resolveProductUpsellCtaText(section.text);
  return section;
}

/**
 * @param {object | null | undefined} product PRODUCT block
 * @param {{ catalogAlign?: boolean }} [options]
 */
export function normalizeProductUpsellProductSubsections(
  product,
  { catalogAlign = false } = {}
) {
  if (!product?.sections) return;

  if (catalogAlign) {
    product.sections = sortProductCatalogSubsections(product.sections);
  }

  for (const sub of product.sections) {
    if (sub.type === "PRICE") {
      sub.setting = sub.setting || {};
      const display = sub.setting.display || {};
      const isFactoryPremiumCard =
        display.layout === PRICE_LAYOUT.PREMIUM_CARD_V2 &&
        (display.subLabel === "One-time · No subscription" ||
          display.subLabel === DEFAULT_PRICE_DISPLAY.subLabel);
      const isLegacyOverrideOnly =
        !display.layout &&
        Boolean(
          sub?.setting?.overrideCss?.desktop || sub?.setting?.overrideCss?.mobile
        );
      sub.setting.display = {
        ...display,
        layout:
          !display.layout || isFactoryPremiumCard || isLegacyOverrideOnly
            ? PRICE_LAYOUT.INLINE_ROW
            : display.layout,
      };
    }

    if (sub.type === "COVER_IMAGE" && catalogAlign) {
      sub.setting = sub.setting || {};
      sub.setting.display = getCoverImageDisplay(sub);
    }

    if (sub.type === "RATING" && catalogAlign) {
      const preserveHidden = !isSectionVisible(sub);
      sub.setting = sub.setting || {};
      sub.setting.display = getRatingDisplay(sub);
      applyCatalogUpsellProductSubsectionAlign(sub);
      if (preserveHidden) {
        setSectionVisibility(sub, false, "block");
      }
    }

    if (sub.type === "DESCRIPTION") {
      const preserveHidden = !isSectionVisible(sub);
      const desktop = sub.setting?.customCSS?.desktop?.display;
      const mobile = sub.setting?.customCSS?.mobile?.display;
      const isFactoryPlaceholder = /^\{\{\s*description\s*\}\}$/i.test(
        String(sub.text || "").trim()
      );
      const isFactoryVisible =
        isFactoryPlaceholder &&
        (desktop === "block" || desktop === "flex") &&
        (mobile === "block" || mobile === "flex");
      const hasExplicitVisibility =
        desktop === "none" ||
        mobile === "none" ||
        desktop === "block" ||
        desktop === "flex" ||
        mobile === "block" ||
        mobile === "flex";
      if (isFactoryVisible || !hasExplicitVisibility) {
        setSectionVisibility(sub, false, "block");
      }
      if (catalogAlign) {
        applyCatalogUpsellProductSubsectionAlign(sub);
      }
      if (preserveHidden) {
        setSectionVisibility(sub, false, "block");
      }
    }

    if (sub.type === "TITLE" && catalogAlign) {
      const preserveHidden = !isSectionVisible(sub);
      applyCatalogUpsellProductSubsectionAlign(sub);
      if (preserveHidden) {
        setSectionVisibility(sub, false, "flex");
      }
    }

    if (
      (sub.type === "PRICE" ||
        sub.type === "VARIANT_PICKER" ||
        sub.type === "QUANTITY_PICKER" ||
        sub.type === "COVER_IMAGE") &&
      catalogAlign
    ) {
      const preserveHidden = !isSectionVisible(sub);
      if (preserveHidden) {
        setSectionVisibility(sub, false, sub.type === "COVER_IMAGE" ? "block" : "flex");
      }
    }

    if (sub.type === "VARIANT_PICKER") {
      sub.setting = sub.setting || {};
      const display = sub.setting.display || {};
      sub.setting.display = {
        ...DEFAULT_VARIANT_PICKER_DISPLAY,
        ...display,
        label:
          display.label != null && String(display.label).trim() !== ""
            ? String(display.label).trim()
            : "",
      };
      if (sub.setting.label != null) {
        delete sub.setting.label;
      }
    }

    if (sub.type === "BUTTON") {
      ensureProductUpsellAddToCartButtonSettings(sub);
    }
  }
}

/**
 * Normalize top-level and nested product add-to-cart buttons.
 * @param {object[] | null | undefined} sections
 * @param {string} [buttonText]
 */
export function normalizeProductUpsellButtonSections(
  sections,
  buttonText = PRODUCT_UPSELL_PRIMARY_CTA_TEXT
) {
  if (!Array.isArray(sections)) return;

  for (const section of sections) {
    if (section?.type === "BUTTON") {
      ensureProductUpsellAddToCartButtonSettings(section);
      if (isProductAddToCartButton(section)) {
        section.text = resolveProductUpsellCtaText(section.text, buttonText);
      }
    }
    if (section?.type === "PRODUCT") {
      normalizeProductUpsellProductSubsections(section, { catalogAlign: false });
    }
  }
}

/**
 * Apply product upsell defaults across all schema steps.
 * @param {object} popup
 * @param {{ catalogAlign?: boolean }} [options]
 */
export function normalizeProductUpsellRecommendationPopup(popup, options = {}) {
  if (!popup || !isStorefrontProductPopup(popup)) return popup;

  const clone =
    typeof structuredClone === "function"
      ? structuredClone(popup)
      : JSON.parse(JSON.stringify(popup));

  clone.generalSettings = clone.generalSettings || {};
  if (!clone.generalSettings.productShellLayout) {
    clone.generalSettings.productShellLayout = PRODUCT_SHELL_LAYOUT.VERTICAL;
  }

  const steps = clone.schema;
  if (!Array.isArray(steps)) return clone;

  for (const step of steps) {
    const sections = step?.section || step?.sections;
    if (!Array.isArray(sections)) continue;

    for (const section of sections) {
      if (section?.type === "PRODUCT") {
        normalizeProductUpsellProductSubsections(section, {
          catalogAlign: options.catalogAlign === true,
        });
        section.setting = section.setting || {};
        if (!section.setting.layoutFlow) {
          section.setting.layoutFlow = true;
        }
        ensureProductShellLayout(section, clone);
      }
    }
    normalizeProductUpsellButtonSections(sections);
  }

  return clone;
}
